import React from 'react';

import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { FlagsProvider } from '@atlaskit/flag';

import { expValEquals } from '@confluence/feature-experiments';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import {
	PROMPT_WHITEBOARD_COMPETITOR_LINK_DISMISSED,
	PROMPT_DATABASE_COMPETITOR_LINK,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSpaceEnabledContentTypes } from '@confluence/create-blank-fabric-page/entry-points/useSpaceEnabledContentTypes.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { useIsAllowedToCreateWhiteboard } from '@confluence/content-types-utils/entry-points/useIsAllowedToCreateWhiteboard.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { useIsEligibleDatabaseCompetitor } from './useIsEligibleDatabaseCompetitor.ts';
import { PromptPopup } from './PromptPopup.tsx';
import type { PromptContentType, LinkType } from './PromptCompetitorLinkTypes.ts';

export type PromptComponentContainerProps = {
	sourceUrl: string;
	linkType?: string;
	editorAppearance?: string;
};

const styles = cssMap({
	promptContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		marginRight: token('space.025'),
		marginLeft: token('space.025'),
	},
});

const VALID_LINK_TYPES: LinkType[] = ['inline', 'card', 'embed'];

const WHITEBOARD_COMPETITORS = [
	{ name: 'Miro', domains: [/miro\.com/i] },
	{ name: 'Mural', domains: [/mural\.co/i] },
	{ name: 'Lucid', domains: [/lucid\.co/i, /lucid\.app/i] },
	{
		name: 'Microsoft Whiteboard',
		domains: [/whiteboard\.office\.com/i, /wbd\.ms/i, /whiteboard\.cloud\.microsoft/i],
	},
	{ name: 'Figjam', domains: [/figma\.com\/board/i] },
	{ name: 'Google Jamboard', domains: [/jamboard\.google\.com/i] }, // Note: Google Jamboard is EOL
	{ name: 'Excalidraw', domains: [/excalidraw\.com/i] },
	{ name: 'Klaxoon', domains: [/klaxoon\.com/i] },
];

const getCompetitorContentType = (
	whiteboardCompetitorName: string | undefined,
	databaseCompetitorName: string | undefined,
): PromptContentType | undefined => {
	if (whiteboardCompetitorName) {
		return 'whiteboard';
	}
	if (databaseCompetitorName) {
		return 'database';
	}
	return undefined;
};

const PromptPopupContainerInner = ({
	sourceUrl,
	linkType,
	editorAppearance,
}: PromptComponentContainerProps) => {
	const {
		data: onboardingStateData,
		loading,
		error,
	} = useGetOnboardingState(
		[
			PROMPT_WHITEBOARD_COMPETITOR_LINK_DISMISSED.PROMPT_DISMISSED,
			PROMPT_DATABASE_COMPETITOR_LINK.DISMISSED,
		],
		false, // skip
	);
	const { isPromptDismissed: isPromptWhiteboardDismissed, isPromptDatabaseDismissed } =
		deserializeState(onboardingStateData);

	// Whiteboard competitor eligibility check
	// isCreateWhiteboardEnabled checks if whiteboards are enabled for the space
	const spaceKey = useSpaceKey();
	const { isCreateWhiteboardEnabled } = useSpaceEnabledContentTypes(spaceKey ?? undefined);

	// isWhiteboardFeatureEnabled allows us to check if particular "whiteboard features" are enabled
	// in this case, we are checking if the entire "whiteboard feature" is enabled AND not opted out
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardsEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');

	// useIsAllowedToCreateWhiteboard checks user permissions to create a whiteboard
	const isAllowedToCreateWhiteboard = useIsAllowedToCreateWhiteboard();
	const isEligibleCreateNewWhiteboard =
		isCreateWhiteboardEnabled && isWhiteboardsEnabled && isAllowedToCreateWhiteboard;
	const whiteboardCompetitor = WHITEBOARD_COMPETITORS.find((competitor) =>
		competitor.domains.some((regex) => regex.test(sourceUrl)),
	);
	const whiteboardCompetitorName = isEligibleCreateNewWhiteboard
		? whiteboardCompetitor?.name
		: undefined;

	const { databaseCompetitorName } = useIsEligibleDatabaseCompetitor(sourceUrl);

	const competitorContentType = getCompetitorContentType(
		whiteboardCompetitorName,
		databaseCompetitorName,
	);

	const competitorName = whiteboardCompetitorName || databaseCompetitorName;

	if (!competitorContentType || !competitorName) {
		return null;
	}

	const validLinkType =
		linkType && VALID_LINK_TYPES.includes(linkType as LinkType)
			? (linkType as LinkType)
			: undefined;

	const contentTypeConfig = {
		database: {
			isFeatureEnabled: () =>
				expValEquals('cc_fd_prompt_database_competitor_link', 'cohort', 'experiment'),
			isDismissed: isPromptDatabaseDismissed,
		},
		whiteboard: {
			// config to exclude tenants or users from the feature
			// if not excluded, `cc_fd_prompt_competitor_link_config` will return `true`
			// `true` is the default value
			isFeatureEnabled: () =>
				StatsigConfigurations.getBoolean('cc_fd_prompt_competitor_link_config', true),
			isDismissed: isPromptWhiteboardDismissed,
		},
	};

	const { isFeatureEnabled, isDismissed } = contentTypeConfig[competitorContentType];

	return (
		!loading &&
		!error &&
		!isDismissed &&
		isFeatureEnabled() && (
			<Box xcss={styles.promptContainer} data-vc="competitor-link-prompt">
				<PromptPopup
					competitorName={competitorName}
					linkType={validLinkType}
					contentType={competitorContentType}
					editorAppearance={editorAppearance}
				/>
			</Box>
		)
	);
};

// Wrapper that provides FlagsProvider context
// FlagsProvider stays mounted even when inner content is dismissed,
// allowing flags to persist
export const PromptPopupContainer = (props: PromptComponentContainerProps) => (
	<FlagsProvider>
		<PromptPopupContainerInner {...props} />
	</FlagsProvider>
);
